import React, { useCallback, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add,
  CalendarMonth,
  ChevronLeft,
  ChevronRight,
  DeleteOutline,
  EventNote,
  FitnessCenter,
  Groups,
  LocationOnOutlined,
  SportsSoccer,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { alpha } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import {
  useCreatePlanningEvent,
  useDeletePlanningEvent,
  usePlanningCalendar,
} from "../hooks/queries/usePlanning";
import {
  PLANNING_ENTITY_TYPES,
  addPlanningDays,
  canManagePlanning,
  getPlanningItemDate,
  findAuthorizedPlanningTeam,
  groupPlanningItemsByDay,
  startOfPlanningWeek,
  toLocalDateKey,
} from "../utils/planning";
import colors from "../design-system/colors";
import usePageTitle from "../hooks/usePageTitle";
import BuscarEquipo from "../components/BuscarEquipo";
import { getMyPlanningTeams } from "../services/teamManagementService";

const OFFICIAL_MATCH_TYPE = "partidoOficial";

const EVENT_META = {
  [OFFICIAL_MATCH_TYPE]: {
    icon: SportsSoccer,
    color: colors.info[700],
    background: colors.info[50],
  },
  [PLANNING_ENTITY_TYPES.TRAINING]: {
    icon: FitnessCenter,
    color: colors.primary[700],
    background: colors.primary[50],
  },
  [PLANNING_ENTITY_TYPES.FRIENDLY_MATCH]: {
    icon: SportsSoccer,
    color: colors.warning[800],
    background: colors.warning[50],
  },
  [PLANNING_ENTITY_TYPES.WEEKLY_PLAN]: {
    icon: EventNote,
    color: "#6a1b9a",
    background: "#f3e5f5",
  },
};

const EMPTY_FORM = {
  tipoEntidad: PLANNING_ENTITY_TYPES.TRAINING,
  titulo: "",
  fecha: "",
  horaInicio: "18:00",
  horaFin: "19:30",
  ubicacion: "",
  oponente: "",
  descripcion: "",
};

const combineDateAndTime = (date, time = "00:00") => {
  if (!date) return null;
  if (String(date).includes("T")) return date;
  return `${date}T${time || "00:00"}:00`;
};

const normalizeOfficialMatch = (match, index) => ({
  ...match,
  id:
    match.id ??
    match.idPartido ??
    match.codacta ??
    `official-${index}`,
  tipoEntidad: OFFICIAL_MATCH_TYPE,
  titulo:
    match.titulo ??
    `${match.equipoLocal ?? match.local ?? "Local"} - ${
      match.equipoVisitante ?? match.visitante ?? "Visitante"
    }`,
  fechaInicio:
    match.fechaInicio ??
    combineDateAndTime(match.fechaISO ?? match.fecha, match.hora),
  ubicacion: match.ubicacion ?? match.estadio ?? match.campo ?? "",
  esOficial: true,
});

const getTimeLabel = (item, locale) => {
  const rawDate = getPlanningItemDate(item);
  if (!rawDate) return "";
  const date = new Date(rawDate);
  if (Number.isNaN(date.getTime())) return item.hora ?? "";
  return new Intl.DateTimeFormat(locale, {
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

function PlanningItemCard({ item, onDelete, isDeleting }) {
  const { t, i18n } = useTranslation("planning");
  const meta = EVENT_META[item.tipoEntidad] ?? EVENT_META[OFFICIAL_MATCH_TYPE];
  const Icon = meta.icon;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.5,
        border: `1px solid ${colors.neutral[200]}`,
        borderLeft: `4px solid ${meta.color}`,
        borderRadius: 2,
        backgroundColor: (theme) => theme.palette.mode === "dark" ? alpha(meta.color, .2) : meta.background,
      }}
    >
      <Stack direction="row" spacing={1.25} alignItems="flex-start">
        <Box
          sx={{
            width: 40,
            height: 40,
            flex: "0 0 40px",
            display: "grid",
            placeItems: "center",
            borderRadius: 1.5,
            color: meta.color,
            backgroundColor: 'background.paper',
          }}
        >
          <Icon fontSize="small" />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="subtitle2" fontWeight={800}>
              {item.titulo}
            </Typography>
            {!item.esOficial && (
              <Tooltip title={t("actions.delete")}>
                <IconButton
                  size="small"
                  aria-label={t("actions.delete")}
                  disabled={isDeleting}
                  onClick={() => onDelete(item)}
                  sx={{ minWidth: 44, minHeight: 44, color: colors.error[700] }}
                >
                  <DeleteOutline fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
          <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
            <Chip
              size="small"
              label={t(`types.${item.tipoEntidad}`)}
              sx={{
                height: 22,
                color: meta.color,
                backgroundColor: 'background.paper',
                fontWeight: 700,
              }}
            />
            {getTimeLabel(item, i18n.language) && (
              <Chip
                size="small"
                label={getTimeLabel(item, i18n.language)}
                sx={{ height: 22, backgroundColor: 'background.paper' }}
              />
            )}
          </Stack>
          {item.ubicacion && (
            <Stack direction="row" spacing={0.5} alignItems="center" mt={1}>
              <LocationOnOutlined
                sx={{ fontSize: 16, color: 'text.secondary' }}
              />
              <Typography variant="caption" color="text.secondary">
                {item.ubicacion}
              </Typography>
            </Stack>
          )}
          {item.descripcion && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 1, whiteSpace: "pre-line" }}
            >
              {item.descripcion}
            </Typography>
          )}
        </Box>
      </Stack>
    </Paper>
  );
}

function PlanningDialog({
  open,
  initialDate,
  scope,
  saving,
  onClose,
  onSubmit,
}) {
  const { t } = useTranslation("planning");
  const [form, setForm] = useState({ ...EMPTY_FORM, fecha: initialDate });
  const [submitted, setSubmitted] = useState(false);

  React.useEffect(() => {
    if (open) {
      setForm({ ...EMPTY_FORM, fecha: initialDate });
      setSubmitted(false);
    }
  }, [initialDate, open]);

  const updateField = (field) => (event) =>
    setForm((current) => ({ ...current, [field]: event.target.value }));

  const isFriendly =
    form.tipoEntidad === PLANNING_ENTITY_TYPES.FRIENDLY_MATCH;
  const isWeeklyPlan =
    form.tipoEntidad === PLANNING_ENTITY_TYPES.WEEKLY_PLAN;
  const hasErrors =
    !form.titulo.trim() ||
    !form.fecha ||
    (isFriendly && !form.oponente.trim());

  const handleSubmit = () => {
    setSubmitted(true);
    if (hasErrors) return;

    const startTime = isWeeklyPlan ? "00:00" : form.horaInicio;
    const endTime = isWeeklyPlan ? "23:59" : form.horaFin;
    onSubmit({
      tipoEntidad: form.tipoEntidad,
      titulo: form.titulo.trim(),
      fechaInicio: combineDateAndTime(form.fecha, startTime),
      fechaFin: combineDateAndTime(form.fecha, endTime),
      ubicacion: form.ubicacion.trim(),
      oponente: isFriendly ? form.oponente.trim() : null,
      descripcion: form.descripcion.trim(),
      teamId: scope?.teamId ?? null,
      seasonId: scope?.seasonId ?? null,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="planning-dialog-title"
    >
      <DialogTitle id="planning-dialog-title">{t("dialog.title")}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <TextField
            select
            fullWidth
            label={t("form.type")}
            value={form.tipoEntidad}
            onChange={updateField("tipoEntidad")}
          >
            {Object.values(PLANNING_ENTITY_TYPES).map((type) => (
              <MenuItem key={type} value={type}>
                {t(`types.${type}`)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            required
            label={t("form.title")}
            value={form.titulo}
            onChange={updateField("titulo")}
            error={submitted && !form.titulo.trim()}
            helperText={
              submitted && !form.titulo.trim() ? t("validation.required") : ""
            }
          />
          {isFriendly && (
            <TextField
              fullWidth
              required
              label={t("form.opponent")}
              value={form.oponente}
              onChange={updateField("oponente")}
              error={submitted && !form.oponente.trim()}
              helperText={
                submitted && !form.oponente.trim()
                  ? t("validation.required")
                  : ""
              }
            />
          )}
          <TextField
            fullWidth
            required
            type="date"
            label={t("form.date")}
            value={form.fecha}
            onChange={updateField("fecha")}
            error={submitted && !form.fecha}
            helperText={submitted && !form.fecha ? t("validation.required") : ""}
            InputLabelProps={{ shrink: true }}
          />
          {!isWeeklyPlan && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="time"
                  label={t("form.startTime")}
                  value={form.horaInicio}
                  onChange={updateField("horaInicio")}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="time"
                  label={t("form.endTime")}
                  value={form.horaFin}
                  onChange={updateField("horaFin")}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          )}
          <TextField
            fullWidth
            label={t("form.location")}
            value={form.ubicacion}
            onChange={updateField("ubicacion")}
          />
          <TextField
            fullWidth
            multiline
            minRows={3}
            label={t("form.notes")}
            value={form.descripcion}
            onChange={updateField("descripcion")}
            helperText={t("form.notesHelp")}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} disabled={saving}>
          {t("actions.cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving}
          startIcon={
            saving ? <CircularProgress color="inherit" size={18} /> : <Add />
          }
          sx={{ minHeight: 44 }}
        >
          {saving ? t("actions.saving") : t("actions.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const PlanningPageModern = ({ usuario }) => {
  const { t, i18n } = useTranslation("planning");
  const navigate = useNavigate();
  const [weekStart, setWeekStart] = useState(() =>
    startOfPlanningWeek(new Date())
  );
  const [dialogOpen, setDialogOpen] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [resolvingTeam, setResolvingTeam] = useState(false);
  usePageTitle(t("pageTitle"));

  const canManage = canManagePlanning(usuario);
  const selectedScope = useMemo(
    () =>
      selectedTeam
        ? {
            teamId: selectedTeam.id,
            seasonId: selectedTeam.seasonId,
            equipoNombre: selectedTeam.name,
          }
        : null,
    [selectedTeam]
  );
  const weekEnd = useMemo(() => addPlanningDays(weekStart, 6), [weekStart]);
  const queryParams = useMemo(
    () => ({
      from: toLocalDateKey(weekStart),
      to: toLocalDateKey(weekEnd),
      teamId: selectedScope?.teamId,
      seasonId: selectedScope?.seasonId,
    }),
    [selectedScope, weekEnd, weekStart]
  );

  // Changing the internal team UUID changes the react-query cache key, so switching
  // teams always refetches its own calendar and keeps each team's planning
  // independent from the others.
  const calendarQuery = usePlanningCalendar(queryParams, navigate, {
    enabled: canManage && Boolean(selectedScope?.teamId),
    retry: 1,
  });
  const createEvent = useCreatePlanningEvent(navigate);
  const deleteEvent = useDeletePlanningEvent(navigate);

  const scope = calendarQuery.data?.scope ?? selectedScope;
  const handleTeamChange = useCallback(
    async (temporadaId, competicionId, grupoId, equipoId, equipo, federacionId) => {
      setSelectedTeam(null);
      setResolvingTeam(true);
      try {
        const planningTeams = await getMyPlanningTeams();
        const authorizedTeam = findAuthorizedPlanningTeam(
          planningTeams,
          {
            externalTeamId: equipoId,
            seasonId: temporadaId,
            federationId: federacionId,
          }
        );

        if (!authorizedTeam) {
          setFeedback({
            severity: "error",
            message: t("feedback.teamNotLinked"),
          });
          return;
        }

        setSelectedTeam({
          id: authorizedTeam.id,
          name: authorizedTeam.name,
          federationTeamId: authorizedTeam.external_team_id,
          seasonId: authorizedTeam.season_id,
          federationId: authorizedTeam.federation_id,
          competitionId: authorizedTeam.competition_id,
          groupId: authorizedTeam.group_id,
        });
      } catch {
        setFeedback({
          severity: "error",
          message: t("feedback.teamResolutionError"),
        });
      } finally {
        setResolvingTeam(false);
      }
    },
    [t]
  );
  const days = useMemo(
    () => Array.from({ length: 7 }, (_, index) => addPlanningDays(weekStart, index)),
    [weekStart]
  );
  const items = useMemo(
    () => [
      ...(calendarQuery.data?.matches ?? []).map(normalizeOfficialMatch),
      ...(calendarQuery.data?.events ?? []),
    ],
    [calendarQuery.data]
  );
  const groupedItems = useMemo(() => groupPlanningItemsByDay(items), [items]);
  const stats = useMemo(
    () => ({
      official: items.filter(({ tipoEntidad }) => tipoEntidad === OFFICIAL_MATCH_TYPE)
        .length,
      training: items.filter(
        ({ tipoEntidad }) => tipoEntidad === PLANNING_ENTITY_TYPES.TRAINING
      ).length,
      plans: items.filter(
        ({ tipoEntidad }) => tipoEntidad === PLANNING_ENTITY_TYPES.WEEKLY_PLAN
      ).length,
    }),
    [items]
  );

  const weekLabel = new Intl.DateTimeFormat(i18n.language, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

  const handleCreate = async (document) => {
    try {
      await createEvent.mutateAsync(document);
      setDialogOpen(false);
      setFeedback({ severity: "success", message: t("feedback.created") });
    } catch {
      setFeedback({ severity: "error", message: t("feedback.createError") });
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(t("actions.confirmDelete"))) return;

    try {
      await deleteEvent.mutateAsync({
        id: item.id,
        partitionKey: item.partitionKey ?? item.tipoEntidad,
      });
      setFeedback({ severity: "success", message: t("feedback.deleted") });
    } catch {
      setFeedback({ severity: "error", message: t("feedback.deleteError") });
    }
  };

  if (!canManage) {
    return (
      <Box component="main" sx={{ maxWidth: 900, mx: "auto", p: { xs: 2, md: 4 } }}>
        <Alert severity="warning">
          <Typography fontWeight={700}>{t("access.title")}</Typography>
          {t("access.description")}
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      className="wp-page"
      component="section"
      sx={{ width: "100%", maxWidth: 1440, mx: "auto", p: { xs: 2, md: 4 } }}
    >
      <Paper
        className="wp-heading"
        elevation={0}
        sx={{
          p: { xs: 2.5, md: 4 },
          mb: 3,
          color: 'text.primary',
          borderRadius: 3,
          backgroundColor: 'background.paper',
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", md: "center" }}
          spacing={3}
        >
          <Box>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <CalendarMonth sx={{ fontSize: 34, color: 'primary.main' }} />
              <Typography component="h1" variant="h4" fontWeight={800}>
                {t("title")}
              </Typography>
            </Stack>
            <Typography sx={{ mt: 1, color: 'text.secondary', maxWidth: 720 }}>
              {t("subtitle")}
            </Typography>
            {scope && (
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" mt={2}>
                {scope.clubNombre && (
                  <Chip
                    icon={<Groups />}
                    label={scope.clubNombre}
                    sx={{ backgroundColor: 'background.paper', fontWeight: 700 }}
                  />
                )}
                {scope.equipoNombre && (
                  <Chip
                    icon={<SportsSoccer />}
                    label={scope.equipoNombre}
                    sx={{ backgroundColor: 'background.paper', fontWeight: 700 }}
                  />
                )}
              </Stack>
            )}
          </Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            disabled={!scope}
            onClick={() => setDialogOpen(true)}
            sx={{ minHeight: 48, px: 3, fontWeight: 800 }}
          >
            {t("actions.add")}
          </Button>
        </Stack>
      </Paper>

      {!calendarQuery.isLoading && !scope && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography fontWeight={700}>{t("selector.title")}</Typography>
          {t("selector.description")}
        </Alert>
      )}

      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 3 },
          mb: 3,
          border: `1px solid ${colors.neutral[200]}`,
          borderRadius: 2,
        }}
      >
        <Typography variant="h6" fontWeight={800}>
          {t("selector.title")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {t("selector.description")}
        </Typography>
        <BuscarEquipo onEquipoSeleccionado={handleTeamChange} />
        {resolvingTeam && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              {t("selector.resolving")}
            </Typography>
          </Stack>
        )}
      </Paper>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: t("stats.official"), value: stats.official, icon: SportsSoccer },
          { label: t("stats.training"), value: stats.training, icon: FitnessCenter },
          { label: t("stats.plans"), value: stats.plans, icon: EventNote },
        ].map(({ label, value, icon: Icon }) => (
          <Grid item xs={12} sm={4} key={label}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                border: `1px solid ${colors.neutral[200]}`,
                borderRadius: 2,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Icon sx={{ color: colors.primary[700] }} />
                <Box>
                  <Typography variant="h5" fontWeight={800}>
                    {value}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {label}
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.5, sm: 2 },
          mb: 3,
          border: `1px solid ${colors.neutral[200]}`,
          borderRadius: 2,
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={1}
        >
          <IconButton
            aria-label={t("actions.previousWeek")}
            onClick={() => setWeekStart(addPlanningDays(weekStart, -7))}
            sx={{ minWidth: 44, minHeight: 44 }}
          >
            <ChevronLeft />
          </IconButton>
          <Box textAlign="center">
            <Typography variant="caption" color="text.secondary">
              {t("week")}
            </Typography>
            <Typography variant="subtitle1" fontWeight={800}>
              {weekLabel.format(weekStart)} - {weekLabel.format(weekEnd)}
            </Typography>
          </Box>
          <IconButton
            aria-label={t("actions.nextWeek")}
            onClick={() => setWeekStart(addPlanningDays(weekStart, 7))}
            sx={{ minWidth: 44, minHeight: 44 }}
          >
            <ChevronRight />
          </IconButton>
        </Stack>
      </Paper>

      {calendarQuery.isLoading && (
        <Box sx={{ py: 8, display: "grid", placeItems: "center" }}>
          <CircularProgress aria-label={t("loading")} />
        </Box>
      )}

      {calendarQuery.isError && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" onClick={() => calendarQuery.refetch()}>
              {t("actions.retry")}
            </Button>
          }
          sx={{ mb: 3 }}
        >
          {t("feedback.loadError")}
        </Alert>
      )}

      {scope && !calendarQuery.isLoading && !calendarQuery.isError && (
        <Grid container spacing={2}>
          {days.map((day) => {
            const dayKey = toLocalDateKey(day);
            const dayItems = groupedItems[dayKey] ?? [];
            const isToday = dayKey === toLocalDateKey(new Date());
            return (
              <Grid item xs={12} md={6} xl={4} key={dayKey}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    minHeight: 180,
                    height: "100%",
                    border: `1px solid ${
                      isToday ? colors.primary[400] : colors.neutral[200]
                    }`,
                    borderRadius: 2,
                    backgroundColor: isToday ? 'action.selected' : 'background.paper',
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                  >
                    <Box>
                      <Typography
                        variant="overline"
                        color={isToday ? "primary.main" : "text.secondary"}
                        fontWeight={800}
                      >
                        {new Intl.DateTimeFormat(i18n.language, {
                          weekday: "long",
                        }).format(day)}
                      </Typography>
                      <Typography variant="h6" fontWeight={800}>
                        {new Intl.DateTimeFormat(i18n.language, {
                          day: "numeric",
                          month: "long",
                        }).format(day)}
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      label={dayItems.length}
                      aria-label={t("dayItemCount", { count: dayItems.length })}
                    />
                  </Stack>
                  <Stack spacing={1.25}>
                    {dayItems.length ? (
                      dayItems.map((item) => (
                        <PlanningItemCard
                          key={`${item.tipoEntidad}-${item.id}`}
                          item={item}
                          onDelete={handleDelete}
                          isDeleting={deleteEvent.isPending}
                        />
                      ))
                    ) : (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ py: 2, textAlign: "center" }}
                      >
                        {t("emptyDay")}
                      </Typography>
                    )}
                  </Stack>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      )}

      <PlanningDialog
        open={dialogOpen}
        initialDate={toLocalDateKey(new Date())}
        scope={scope}
        saving={createEvent.isPending}
        onClose={() => setDialogOpen(false)}
        onSubmit={handleCreate}
      />

      <Snackbar
        open={Boolean(feedback)}
        autoHideDuration={4000}
        onClose={() => setFeedback(null)}
      >
        {feedback && (
          <Alert
            severity={feedback.severity}
            onClose={() => setFeedback(null)}
            variant="filled"
            sx={{ width: "100%" }}
          >
            {feedback.message}
          </Alert>
        )}
      </Snackbar>
    </Box>
  );
};

export default PlanningPageModern;
